import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a paginated results list
 * with support for infinite scroll, pagination, and load-more.
 */
export default class ResultsList extends PaginatedList {

  /** @type {HTMLButtonElement | null} */
  #loadMoreBtn = null;

  /** @type {HTMLElement | null} */
  #loadMoreWrapper = null;

  /** @type {boolean} */
  #isLoadingMore = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.getAttribute('load-mode') === 'load-more') {
      this.#initLoadMore();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#loadMoreBtn?.removeEventListener('click', this.#handleLoadMoreClick);
  }

  // ─── Load More ────────────────────────────────────────────────────────────

  #initLoadMore() {
    this.#loadMoreBtn = this.querySelector('[ref="loadMoreBtn"]');
    this.#loadMoreWrapper = this.querySelector('[ref="loadMoreWrapper"]');

    if (!this.#loadMoreBtn) return;

    this.#loadMoreBtn.addEventListener('click', this.#handleLoadMoreClick);

    // Pre-fetcha la pagina 2 subito in background
    requestIdleCallback(() => this.#prefetchPage(2));
  }

  #handleLoadMoreClick = async () => {
    if (this.#isLoadingMore) return;

    const { grid } = this.refs;
    if (!grid) return;

    // Determina la pagina successiva dall'ultimo card nel DOM
    const allCards = [...grid.querySelectorAll('[ref="cards[]"]')];
    if (!allCards.length) return;

    const lastCard = allCards[allCards.length - 1];
    const currentPage = Number(lastCard.dataset.page ?? 1);
    const nextPage = currentPage + 1;

    const totalPages = Number(this.#loadMoreBtn?.dataset.totalPages ?? 1);

    if (nextPage > totalPages) {
      this.#hideButton();
      return;
    }

    this.#setLoading(true);

    try {
      // Usa la cache di PaginatedList se disponibile, altrimenti fetcha
      if (!this.pages.has(nextPage)) {
        await this.#prefetchPage(nextPage);
      }

      const newItems = this.#getItemsFromCache(nextPage);

      if (!newItems || newItems.length === 0) {
        console.warn('[ResultsList] No items found for page', nextPage);
        return;
      }

      // Appende al grid con animazione staggered
      this.#appendWithAnimation(newItems);

      // Aggiorna il data attribute della pagina corrente sul bottone
      this.#loadMoreBtn?.setAttribute('data-current-page', String(nextPage));

      // Aggiorna il contatore dei prodotti rimanenti
      this.#updateButtonCount();

      // Nascondi il bottone se siamo all'ultima pagina
      if (nextPage >= totalPages) {
        this.#hideButton();
        return;
      }

      // Pre-fetcha la pagina successiva in background
      requestIdleCallback(() => this.#prefetchPage(nextPage + 1));

    } catch (err) {
      console.error('[ResultsList] Load More error:', err);
    } finally {
      this.#setLoading(false);
    }
  };

  /**
   * Fetcha e salva una pagina nella cache di PaginatedList.
   * Riusa sectionRenderer esattamente come fa la classe padre.
   *
   * @param {number} pageNumber
   */
  async #prefetchPage(pageNumber) {
    if (this.pages.has(pageNumber)) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNumber));
    url.hash = '';

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      this.pages.set(pageNumber, html);
    } catch (err) {
      console.error('[ResultsList] Prefetch error for page', pageNumber, err);
    }
  }

  /**
   * Estrae i card dalla cache HTML di una pagina.
   * Replica la stessa logica di #getGridForPage in PaginatedList.
   *
   * @param {number} page
   * @returns {Element[] | null}
   */
  #getItemsFromCache(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return null;

    const doc = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridEl = doc.querySelector('[ref="grid"]');
    if (!gridEl) return null;

    const items = [...gridEl.querySelectorAll(':scope > [ref="cards[]"]')];
    return items.length ? items : null;
  }

  /**
   * Appende i nuovi elementi al grid con animazione staggered.
   *
   * @param {Element[]} items
   */
  #appendWithAnimation(items) {
    const { grid } = this.refs;
    if (!grid) return;

    const fragment = document.createDocumentFragment();

    items.forEach((item, index) => {
      item.style.setProperty('--load-more-index', String(index));
      item.classList.add('product-grid__item--load-more-entering');
      fragment.appendChild(item);
    });

    grid.appendChild(fragment);

    items.forEach((item) => {
      item.addEventListener(
        'animationend',
        () => item.classList.remove('product-grid__item--load-more-entering'),
        { once: true }
      );
    });
  }

  /**
   * Aggiorna il contatore dei prodotti rimanenti nel bottone.
   */
  #updateButtonCount() {
    const countEl = this.#loadMoreBtn?.querySelector('.load-more-btn__count');
    if (!countEl) return;

    const { grid } = this.refs;
    if (!grid) return;

    const totalProducts = Number(this.#loadMoreBtn?.dataset.totalProducts ?? 0);
    const loadedCount = grid.querySelectorAll('[ref="cards[]"]').length;
    const remaining = totalProducts - loadedCount;

    countEl.textContent = remaining > 0 ? `\u2014\u00a0${remaining} prodotti` : '';
  }

  /**
   * Imposta lo stato di loading sul bottone.
   *
   * @param {boolean} state
   */
  #setLoading(state) {
    this.#isLoadingMore = state;
    if (!this.#loadMoreBtn) return;
    this.#loadMoreBtn.disabled = state;
    this.#loadMoreBtn.setAttribute('aria-busy', String(state));
    this.#loadMoreBtn.classList.toggle('load-more-btn--loading', state);
  }

  /**
   * Rimuove il bottone dal DOM quando non ci sono più pagine.
   */
  #hideButton() {
    this.#loadMoreWrapper?.remove();
    this.#loadMoreBtn = null;
    this.#loadMoreWrapper = null;
  }

  // ─── Layout (invariato rispetto all'originale) ────────────────────────────

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /**
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}